import { t as tr } from './i18n';
import { isPartial, shortfall } from './coverage';
import { fmtNum, lei } from './format';
import { money, perUnitMonth, regimeMismatch } from './package';
import { billedQty, termsOf } from './terms';
import type { Offer, Rfq, RfqItem, Send, SupplierRef } from './types';

/**
 * Prețul unitar în lei care intră în comparație, sau null. O ofertă de alt
 * regim decât cel cerut (chirie față de cumpărare) nu intră: rămâne vizibilă în
 * tabel, cu motivul, dar nu câștigă și nu pierde nimic.
 */
export function comparablePrice(offer: Offer, item: RfqItem): number | null {
  const p = offer.prices[item.id];
  if (p == null) return null;
  return regimeMismatch(item, offer) ? null : p;
}

/**
 * Prețul pe unitatea cerută după comanda minimă și ambalaj: 30 lei/sac cu
 * minim 100, când ceri 80, înseamnă 37,50 lei pentru fiecare sac de care ai
 * nevoie. Fără condiții, exact prețul listat. Pe el se alege verdele.
 */
export function effectivePrice(offer: Offer, item: RfqItem): number | null {
  const p = comparablePrice(offer, item);
  if (p == null) return null;
  const terms = termsOf(offer, item);
  return terms ? (p * billedQty(item.qty, terms)) / item.qty : p;
}

/** Valoarea plătită pe poziție, în lei: prețul listat × cantitatea plătită. */
export function lineCost(offer: Offer, item: RfqItem): number | null {
  const p = comparablePrice(offer, item);
  return p == null ? null : p * billedQty(item.qty, termsOf(offer, item));
}

/**
 * Ce se plătește peste prețurile pozițiilor, în lei: transportul separat și
 * celelalte costuri ale comenzii. `unknown` = transportul e separat, dar suma
 * nu e în ofertă, deci costul total e doar o limită de jos.
 */
export function extraCost(offer: Offer): { lei: number; unknown: boolean } {
  const fx = offer.currency === 'EUR' ? (offer.fxRate ?? 1) : 1;
  const extra = offer.transport === 'extra';
  const transport = extra ? (offer.transportCost ?? 0) : 0;
  const others = (offer.extras ?? []).reduce((n, e) => n + e.amount, 0);
  return { lei: (transport + others) * fx, unknown: extra && offer.transportCost == null };
}

/** Transportul unei oferte, cum apare în tabel: „inclus”, „1.200 lei”, „nu spune”. */
export function transportText(offer: Offer): string {
  if (offer.transport === 'included') return 'inclus';
  if (offer.transport !== 'extra') return tr('nu spune');
  if (offer.transportCost == null) return tr('separat, suma nu se știe');
  const fx = offer.currency === 'EUR' ? (offer.fxRate ?? 1) : 1;
  return lei(offer.transportCost * fx);
}

/** Celelalte costuri ale unei oferte, pe scurt: „descărcare macara 400 lei”. */
export function extrasText(offer: Offer): string {
  const fx = offer.currency === 'EUR' ? (offer.fxRate ?? 1) : 1;
  return (offer.extras ?? []).map((e) => `${e.label} ${lei(e.amount * fx)}`).join(', ');
}

/**
 * Măcar o ofertă spune ceva despre transport sau are costuri peste prețuri.
 * Altfel tabelul rămâne cu un singur rând de total, ca înainte.
 */
export function hasCostInfo(a: Analysis): boolean {
  return a.cols.some(
    (c) => (c.send.offer!.transport ?? '') !== '' || (c.send.offer!.extras ?? []).length > 0,
  );
}

/**
 * Logica tabelului comparativ, portată 1:1 din `analyse()` din demo-ul vanilla.
 * Rezultatele trebuie să rămână identice — vezi tests/analyse.test.ts.
 */

export interface AnalyseColumn {
  send: Send;
  supplier: SupplierRef;
  /** prețurile listate × cantitățile cerute, ca și cum n-ar exista condiții */
  listedTotal: number;
  /** valoarea plătită pe poziții, cu comanda minimă și ambalajul, fără TVA */
  subtotal: number;
  /** transportul separat și celelalte costuri ale comenzii, în lei */
  extras: number;
  /** transportul se plătește separat, dar suma nu se știe: `total` e prea mic */
  costUnknown: boolean;
  /** costul total fără TVA (poziții + transport + alte costuri); are sens doar dacă `complete` */
  total: number;
  /** oferta are preț pe toate pozițiile cerute */
  complete: boolean;
  /**
   * măcar o poziție e ofertată pe mai puțin decât s-a cerut: totalul se arată,
   * dar oferta nu câștigă (n-ar compara aceeași cantitate)
   */
  partial: boolean;
}

export interface Analysis {
  /** o coloană pentru fiecare furnizor care a răspuns, în ordinea trimiterilor */
  cols: AnalyseColumn[];
  /**
   * cel mai mic preț pe unitatea cerută, pe fiecare poziție (după comanda minimă
   * și ambalaj); null dacă e cotată de mai puțin de 2 furnizori
   */
  bestRow: Record<string, number | null>;
  /**
   * cel mai mic cost total sigur: dintre ofertele complete cu costul știut, și
   * doar dacă nicio ofertă cu transport necunoscut n-ar putea fi sub el; null
   * dacă e o singură ofertă completă sau dacă totalurile nu sunt complete încă
   */
  bestTotal: number | null;
  /**
   * ofertele complete cu transport necunoscut care ar putea fi cele mai ieftine
   * (limita lor de jos e sub cel mai mic cost sigur): până răspund, nimeni nu e
   * „cel mai ieftin”
   */
  unsure: AnalyseColumn[];
  /**
   * totalul dacă fiecare poziție se comandă de la cel mai ieftin, cu transportul
   * și costurile fiecărui furnizor folosit; null dacă nu se poate calcula
   */
  mix: number | null;
  /** `mix` ia ceva de la un furnizor cu transportul necunoscut: e doar o limită de jos */
  mixUnknown: boolean;
  /** doar ofertele complete și pe toată cantitatea: ele se compară la total */
  full: AnalyseColumn[];
}

export function analyse(rfq: Rfq): Analysis {
  const resp = rfq.sends.filter((s) => s.offer);

  const cols: AnalyseColumn[] = resp.map((s) => {
    let listedTotal = 0;
    let subtotal = 0;
    let complete = true;
    rfq.items.forEach((it) => {
      const p = comparablePrice(s.offer!, it);
      if (p == null) complete = false;
      else {
        listedTotal += p * it.qty;
        subtotal += lineCost(s.offer!, it)!;
      }
    });
    const extra = extraCost(s.offer!);
    return {
      send: s,
      supplier: s.supplier,
      listedTotal,
      subtotal,
      extras: extra.lei,
      costUnknown: extra.unknown,
      total: subtotal + extra.lei,
      complete,
      partial: isPartial(s.offer!, rfq.items),
    };
  });

  const bestRow: Record<string, number | null> = {};
  rfq.items.forEach((it) => {
    // O ofertă pe jumătate din cantitate nu e „cel mai bun preț” pe poziție.
    const vals = resp
      .filter((s) => !shortfall(s.offer!, it))
      .map((s) => effectivePrice(s.offer!, it))
      .filter((v): v is number => v != null);
    bestRow[it.id] = vals.length > 1 ? Math.min(...vals) : null;
  });

  const full = cols.filter((c) => c.complete && !c.partial);
  // Transportul necunoscut face din total doar o limită de jos: o astfel de
  // ofertă nu câștigă, și nici nu lasă pe altul să câștige cât timp ar putea fi sub el.
  const known = full.filter((c) => !c.costUnknown).map((c) => c.total);
  const sure = known.length ? Math.min(...known) : Infinity;
  const unsure = full.length > 1 ? full.filter((c) => c.costUnknown && c.total < sure) : [];
  const bestTotal = full.length > 1 && known.length && !unsure.length ? sure : null;

  let mix: number | null = null;
  let mixUnknown = false;
  if (resp.length > 1 && rfq.items.every((it) => bestRow[it.id] != null)) {
    // Împărțită, comanda plătește transportul fiecărui furnizor de la care ia ceva.
    const used = new Set<AnalyseColumn>();
    mix = rfq.items.reduce((a, it) => {
      used.add(
        cols.find(
          (c) => !shortfall(c.send.offer!, it) && effectivePrice(c.send.offer!, it) === bestRow[it.id],
        )!,
      );
      return a + bestRow[it.id]! * it.qty;
    }, 0);
    for (const c of used) mix += c.extras;
    mixUnknown = [...used].some((c) => c.costUnknown);
  }

  return { cols, bestRow, bestTotal, unsure, mix, mixUnknown, full };
}

export interface PriceCell {
  /** prețul unitar formatat */
  unit: string;
  /** valoarea pe poziție, „1.234 lei” */
  line: string;
  /** cel mai bun preț pe rând → se marchează cu verde */
  best: boolean;
}

/** O celulă din tabelul comparativ; null dacă furnizorul nu a cotat poziția. */
export function priceCell(a: Analysis, item: RfqItem, col: AnalyseColumn): PriceCell | null {
  const offer = col.send.offer!;
  const p = comparablePrice(offer, item);
  if (p == null) return null;
  const best =
    a.bestRow[item.id] != null &&
    !shortfall(offer, item) &&
    effectivePrice(offer, item) === a.bestRow[item.id];
  return { unit: fmtNum(p), line: lei(lineCost(offer, item)!), best };
}

export interface Summary {
  /** oferta completă cu cel mai mic cost total */
  win: AnalyseColumn;
  /** oferta completă cu cele mai mici prețuri listate; poate fi alta decât `win` */
  listed: AnalyseColumn;
  /** totalul pe poziții, doar dacă merită arătat (mai mic cu peste 1 leu) */
  mix: number | null;
  /** cât se economisește împărțind comanda; null dacă `mix` e null */
  saving: number | null;
  /** diferența între cea mai mică și cea mai mare ofertă completă cu costul știut; null sub două */
  spread: number | null;
}

/** Cele trei casete de sub tabel („verdict”), scoase din `cmpHtml` ca să fie testabile. */
export function summary(a: Analysis): Summary | null {
  if (!a.full.length) return null;
  const win = a.full.reduce((x, y) => (y.total < x.total ? y : x));
  const listed = a.full.reduce((x, y) => (y.listedTotal < x.listedTotal ? y : x));
  const mix = a.mix != null && a.mix < win.total - 1 ? a.mix : null;
  const saving = mix != null ? win.total - mix : null;
  // între cea mai mică și cea mai mare ofertă cu costul știut: un transport necunoscut ar face diferența prea mică
  const known = a.full.filter((c) => !c.costUnknown).map((c) => c.total);
  const spread = known.length > 1 ? Math.max(...known) - Math.min(...known) : null;
  return { win, listed, mix, saving, spread };
}

/**
 * Ce trebuie să vadă omul înainte să aleagă, dincolo de cifra cea mai mică:
 * prețul listat care pierde la cost total și transportul care poate schimba
 * clasamentul. Gol când comparația e curată.
 */
export function costNotes(a: Analysis): string[] {
  const s = summary(a);
  if (!s || a.full.length < 2) return [];
  const notes: string[] = [];
  const { win, listed } = s;
  // Cu transportul câștigătorului necunoscut nu se poate spune cine iese mai ieftin.
  if (listed !== win && !win.costUnknown && listed.total - win.total >= 1) {
    // de ce pierde: ce se adaugă peste prețurile lui listate
    const why: string[] = [];
    if (listed.subtotal - listed.listedTotal >= 0.01) why.push(tr('comanda minimă'));
    if (listed.extras > 0 || listed.costUnknown) why.push(tr('transportul și costurile pe comandă'));
    notes.push(
      tr(
        '{0} are prețurile listate cele mai mici, dar cu {1} {2} iese mai ieftin cu {3}.',
        listed.supplier.name,
        why.join(tr(' și ')) || tr('costurile peste prețuri'),
        win.supplier.name,
        lei(listed.total - win.total),
      ),
    );
  }
  // Doar câștigătorul contează: oricine altcineva e deja mai scump, chiar fără
  // costurile pe care nu le știm.
  const t = win.send.offer!.transport ?? '';
  if (win.costUnknown) {
    notes.push(
      tr(
        '{0} taxează transportul separat, dar n-a dat suma: totalurile nu sunt complete încă. Întreabă-l cât costă înainte de a decide.',
        win.supplier.name,
      ),
    );
  } else if (t === '' && a.full.some((c) => c !== win && (c.send.offer!.transport ?? '') !== '')) {
    notes.push(
      tr(
        '{0} nu spune nimic despre transport. Dacă se plătește separat, clasamentul se poate schimba.',
        win.supplier.name,
      ),
    );
  }
  return notes;
}

/**
 * La chirie pe set, verdele merge pe costul total: e ce plătești. Dar furnizorii
 * nu ofertează fix suprafața cerută (500 mp cerute, 520 ofertați), așa că cel mai
 * ieftin la total nu e neapărat cel mai ieftin pe mp. Când altul e mai ieftin pe
 * mp, omul trebuie să vadă asta și să decidă dacă îi folosesc mp în plus.
 * Prețul pe mp se compară în lei, cu cursul fiecărei oferte.
 */
export function unitNotes(a: Analysis, items: RfqItem[]): string[] {
  const notes: string[] = [];
  for (const it of items) {
    if (a.bestRow[it.id] == null) continue;
    const rows = a.cols.flatMap((c) => {
      const offer = c.send.offer!;
      const pkg = offer.packages?.[it.id];
      if (!pkg || comparablePrice(offer, it) == null) return [];
      const per = perUnitMonth(pkg);
      if (per == null) return [];
      if (offer.currency === 'EUR' && !offer.fxRate) return [];
      const fx = offer.currency === 'EUR' ? offer.fxRate! : 1;
      return [{ c, offer, pkg, per, perLei: per * fx }];
    });
    const win = rows.find((r) => !shortfall(r.offer, it) && effectivePrice(r.offer, it) === a.bestRow[it.id]);
    if (!win) continue;
    const cheaper = rows
      .filter((r) => r !== win && r.perLei < win.perLei * 0.995)
      .sort((x, y) => x.perLei - y.perLei)[0];
    if (!cheaper) continue;
    const diff = lineCost(cheaper.offer, it)! - lineCost(win.offer, it)!;
    notes.push(
      tr(
        '{0}: {1} e mai ieftin pe {2} ({3}/{2}/lună față de {4}), dar ofertează {5} {2} din {6} {2} cerute. La total, {7} iese mai ieftin cu {8} pe poziția asta.',
        it.desc,
        cheaper.c.supplier.name,
        it.unit,
        money(cheaper.per, cheaper.offer.currency),
        money(win.per, win.offer.currency),
        fmtNum(cheaper.pkg.coveredQty!),
        fmtNum(it.qty),
        win.c.supplier.name,
        lei(diff),
      ),
    );
  }
  return notes;
}
